import { useEffect, useMemo, useState } from "react";
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer } from "react-leaflet";
import { useSession } from "@/auth/session";
import {
  getMobileRouteSessionsOverview,
  listMobileDevices,
  listReadingPeriods,
  type MobileDeviceItem,
  type MobileRouteSessionOverviewItem,
  type MobileRouteSessionsOverview,
  type ReadingPeriodItem,
} from "@/lib/api";

const emptyOverview: MobileRouteSessionsOverview = {
  period: null,
  stats: {
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    totalDurationSeconds: 0,
    totalTrackPoints: 0,
    totalReadingPoints: 0,
  },
  filters: {
    routes: [],
    operators: [],
    devices: [],
    statuses: [],
  },
  rows: [],
};

export default function SeguimientoTerrenoPage() {
  const { session } = useSession();
  const [periods, setPeriods] = useState<ReadingPeriodItem[]>([]);
  const [overview, setOverview] = useState<MobileRouteSessionsOverview>(emptyOverview);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [routeFilter, setRouteFilter] = useState("todos");
  const [operatorFilter, setOperatorFilter] = useState("todos");
  const [deviceFilter, setDeviceFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [devices, setDevices] = useState<MobileDeviceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session) return;
    let cancelled = false;

    async function loadPeriods(): Promise<void> {
      setLoading(true);
      setError("");
      try {
        const [items, devs] = await Promise.all([listReadingPeriods(session), listMobileDevices(session)]);
        if (cancelled) return;
        setPeriods(items);
        setDevices(devs);
        const defaultPeriod = items.find((item) => item.status === "open") ?? items[0];
        if (defaultPeriod) setSelectedPeriodId((current) => current || defaultPeriod.id);
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "No se pudieron cargar los períodos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadPeriods();
    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    if (!session || !selectedPeriodId) return;
    let cancelled = false;

    async function loadOverview(): Promise<void> {
      setLoading(true);
      setError("");
      try {
        const nextOverview = await getMobileRouteSessionsOverview(session, selectedPeriodId);
        if (cancelled) return;
        setOverview(nextOverview);
        setSelectedSessionId((current) =>
          current && nextOverview.rows.some((row) => row.sessionId === current) ? current : nextOverview.rows[0]?.sessionId ?? null,
        );
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "No se pudo cargar seguimiento terreno");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadOverview();
    return () => {
      cancelled = true;
    };
  }, [selectedPeriodId, session]);

  // Auto-refresh cada 30s mientras haya sesiones activas
  useEffect(() => {
    if (!session || !selectedPeriodId || overview.stats.activeSessions === 0) return;

    const interval = setInterval(() => {
      getMobileRouteSessionsOverview(session, selectedPeriodId)
        .then((nextOverview) => {
          setOverview(nextOverview);
        })
        .catch(() => undefined);
    }, 30_000);

    return () => clearInterval(interval);
  }, [session, selectedPeriodId, overview.stats.activeSessions]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return overview.rows.filter((row) => {
      const matchesSearch =
        term.length === 0 ||
        row.routeName.toLowerCase().includes(term) ||
        (row.operatorName ?? "").toLowerCase().includes(term) ||
        row.deviceCode.toLowerCase().includes(term) ||
        row.deviceName.toLowerCase().includes(term);
      const matchesRoute = routeFilter === "todos" || row.routeName === routeFilter;
      const matchesOperator = operatorFilter === "todos" || row.operatorName === operatorFilter;
      const matchesDevice = deviceFilter === "todos" || row.deviceCode === deviceFilter;
      const matchesStatus = statusFilter === "todos" || row.status === statusFilter;
      return matchesSearch && matchesRoute && matchesOperator && matchesDevice && matchesStatus;
    });
  }, [deviceFilter, operatorFilter, overview.rows, routeFilter, search, statusFilter]);

  const selectedSession =
    filteredRows.find((row) => row.sessionId === selectedSessionId) ??
    overview.rows.find((row) => row.sessionId === selectedSessionId) ??
    filteredRows[0] ??
    null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="mb-0.5 flex items-center gap-2 text-sm text-gray-400">
            <span>Reportes</span>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-gray-600">Seguimiento Terreno</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800">Seguimiento Terreno</h1>
          <p className="mt-0.5 text-sm text-gray-500">Seguimiento en tiempo real de rutas activas y revisión de rutas completadas desde la app móvil.</p>
        </div>
        <select
          value={selectedPeriodId}
          onChange={(event) => setSelectedPeriodId(event.target.value)}
          className="cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 focus:border-emerald-400 focus:outline-none"
        >
          {periods.map((period) => (
            <option key={period.id} value={period.id}>
              {period.periodCode} ({period.status})
            </option>
          ))}
        </select>
      </div>

      {error ? <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">{error}</div> : null}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
        <SummaryCard label="Sesiones" value={overview.stats.totalSessions.toString()} icon="ri-route-line" tone="slate" />
        <SummaryCard label="Activas" value={overview.stats.activeSessions.toString()} icon="ri-radar-line" tone="cyan" />
        <SummaryCard label="Cerradas" value={overview.stats.completedSessions.toString()} icon="ri-checkbox-circle-line" tone="emerald" />
        <SummaryCard label="Horas" value={formatHours(overview.stats.totalDurationSeconds)} icon="ri-time-line" tone="amber" />
        <SummaryCard label="Puntos GPS" value={overview.stats.totalTrackPoints.toString()} icon="ri-map-pin-line" tone="blue" />
        <SummaryCard label="Lecturas marcadas" value={overview.stats.totalReadingPoints.toString()} icon="ri-dashboard-line" tone="rose" />
      </div>

      <DeviceSyncPanel devices={devices} />

      <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <div className="absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Buscar por ruta, operador o dispositivo..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-4 text-sm focus:border-emerald-400 focus:outline-none"
            />
          </div>
          <FilterSelect value={routeFilter} onChange={setRouteFilter} placeholder="Todas las rutas" options={overview.filters.routes} />
          <FilterSelect value={operatorFilter} onChange={setOperatorFilter} placeholder="Todos los operadores" options={overview.filters.operators} />
          <FilterSelect value={deviceFilter} onChange={setDeviceFilter} placeholder="Todos los dispositivos" options={overview.filters.devices} />
          <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="Todos los estados" options={overview.filters.statuses} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_.9fr]">
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-700">Sesiones</h2>
              {overview.stats.activeSessions > 0 && (
                <span className="flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
                  </span>
                  EN VIVO
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400">{filteredRows.length} resultados</span>
          </div>
          <div className="max-h-[720px] divide-y divide-gray-100 overflow-y-auto">
            {filteredRows.map((row) => {
              const selected = row.sessionId === selectedSession?.sessionId;
              return (
                <button
                  key={row.sessionId}
                  type="button"
                  onClick={() => setSelectedSessionId(row.sessionId)}
                  className={`w-full px-4 py-4 text-left transition-colors ${selected ? "bg-cyan-50/60" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">{row.routeName}</span>
                        <StatusBadge status={row.status} />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {row.operatorName ?? "Sin operador"} · {row.deviceCode} · {formatDateTime(row.startedAt)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-semibold text-gray-800">{formatDuration(row.durationSeconds)}</div>
                      <div className="text-xs text-gray-400">
                        {row.pointsCount} pts · {row.readingPointsCount} lecturas
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            {filteredRows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500">{loading ? "Cargando sesiones..." : "No hay sesiones para los filtros aplicados."}</div>
            ) : null}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-700">Detalle de sesión</h2>
          </div>
          {selectedSession ? (
            <div className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-3">
                <Detail label="Ruta" value={selectedSession.routeName} />
                <Detail label="Estado" value={selectedSession.status} />
                <Detail label="Operador" value={selectedSession.operatorName ?? "Sin operador"} />
                <Detail label="Dispositivo" value={`${selectedSession.deviceCode} · ${selectedSession.deviceName}`} />
                <Detail label="Inicio" value={formatDateTime(selectedSession.startedAt)} />
                <Detail label="Término" value={selectedSession.endedAt ? formatDateTime(selectedSession.endedAt) : "En curso"} />
                <Detail label="Duración" value={formatDuration(selectedSession.durationSeconds)} />
                <Detail label="Distancia" value={formatDistance(selectedSession.distanceMeters)} />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <MiniMetric label="Puntos" value={selectedSession.pointsCount.toString()} />
                <MiniMetric label="Lecturas" value={selectedSession.readingPointsCount.toString()} />
                <MiniMetric label="Origen" value={selectedSession.source} />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Mapa del recorrido</h3>
                <RouteMap points={selectedSession.points} />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Puntos relevantes</h3>
                <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-100">
                  {selectedSession.points.map((point) => (
                    <div key={`${selectedSession.sessionId}-${point.sequence}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <div>
                        <div className="font-medium capitalize text-gray-700">{translateEventType(point.eventType)}</div>
                        <div className="text-xs text-gray-400">{formatDateTime(point.recordedAt)}</div>
                      </div>
                      <div className="text-right font-mono text-xs text-gray-500">
                        <div>{point.lat.toFixed(5)}</div>
                        <div>{point.lng.toFixed(5)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-12 text-center text-sm text-gray-500">Selecciona una sesión para ver el detalle.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon: string;
  tone: "slate" | "cyan" | "emerald" | "amber" | "blue" | "rose";
}) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-700",
    cyan: "bg-cyan-50 text-cyan-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-blue-700",
    rose: "bg-rose-50 text-rose-700",
  } as const;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${toneMap[tone]}`}>
        <i className={`${icon} text-lg`} />
      </div>
      <div>
        <div className="text-xl font-bold text-gray-800">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: string[];
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 focus:border-emerald-400 focus:outline-none"
    >
      <option value="todos">{placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function StatusBadge({ status }: { status: string }) {
  const label = status === "active" ? "Activa" : status === "completed" ? "Cerrada" : status;
  const className =
    status === "active"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : "border-emerald-200 bg-emerald-50 text-emerald-700";
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${className}`}>{label}</span>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-gray-700">{value}</div>
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-100 px-3 py-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-gray-700">{value}</div>
    </div>
  );
}

function RouteMap({ points }: { points: MobileRouteSessionOverviewItem["points"] }) {
  if (points.length === 0) {
    return (
      <div className="flex h-72 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-400">
        No hay puntos GPS para mostrar.
      </div>
    );
  }

  const routePath = points.map((point) => [point.lat, point.lng] as [number, number]);
  const center = routePath[Math.floor(routePath.length / 2)] ?? routePath[0];
  const startPoint = points[0] ?? null;
  const endPoint = points[points.length - 1] ?? null;
  const readingPoints = points.filter((point) => point.eventType === "reading");

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100">
      <MapContainer center={center} zoom={15} scrollWheelZoom className="h-72 w-full">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Polyline positions={routePath} pathOptions={{ color: "#0f4aa5", weight: 5, opacity: 0.8 }} />
        {startPoint ? (
          <CircleMarker center={[startPoint.lat, startPoint.lng]} radius={8} pathOptions={{ color: "#047857", fillColor: "#10b981", fillOpacity: 0.95 }}>
            <Popup>
              <strong>Inicio</strong>
              <div>{formatDateTime(startPoint.recordedAt)}</div>
            </Popup>
          </CircleMarker>
        ) : null}
        {endPoint && endPoint !== startPoint ? (
          <CircleMarker center={[endPoint.lat, endPoint.lng]} radius={8} pathOptions={{ color: "#b91c1c", fillColor: "#ef4444", fillOpacity: 0.95 }}>
            <Popup>
              <strong>Fin</strong>
              <div>{formatDateTime(endPoint.recordedAt)}</div>
            </Popup>
          </CircleMarker>
        ) : null}
        {readingPoints.map((point) => (
          <CircleMarker
            key={`${point.sequence}-${point.recordedAt}`}
            center={[point.lat, point.lng]}
            radius={6}
            pathOptions={{ color: "#0ea5a8", fillColor: "#22d3ee", fillOpacity: 0.9 }}
          >
            <Popup>
              <strong>Lectura</strong>
              <div>{formatDateTime(point.recordedAt)}</div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

function getSyncStatus(lastSyncAt: string | null): { dot: string; label: string } {
  if (!lastSyncAt) return { dot: "bg-gray-300", label: "Nunca sincronizado" };
  const diffMs = Date.now() - new Date(lastSyncAt).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 30) return { dot: "bg-emerald-400", label: diffMin < 2 ? "Hace un momento" : `Hace ${diffMin} min` };
  const diffHours = Math.floor(diffMs / 3_600_000);
  if (diffHours < 4) return { dot: "bg-amber-400", label: `Hace ${diffHours}h ${diffMin % 60}m` };
  if (diffHours < 24) return { dot: "bg-red-400", label: `Hace ${diffHours} horas` };
  const diffDays = Math.floor(diffHours / 24);
  return { dot: "bg-red-500", label: `Hace ${diffDays} día${diffDays > 1 ? "s" : ""}` };
}

function DeviceSyncPanel({ devices }: { devices: MobileDeviceItem[] }) {
  if (devices.length === 0) return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-gray-700">Estado de dispositivos</h2>
      <div className="flex flex-wrap gap-3">
        {devices.map((device) => {
          const { dot, label } = getSyncStatus(device.lastSyncAt);
          return (
            <div key={device.id} className="flex items-center gap-2.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
              <div>
                <div className="text-xs font-medium text-gray-700">{device.name}</div>
                <div className="text-[10px] text-gray-400">{label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatDuration(value: number | null): string {
  if (value == null) return "Sin cierre";
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function formatHours(value: number): string {
  return `${(value / 3600).toFixed(1)} h`;
}

function formatDistance(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(2)} km`;
  return `${value} m`;
}

function translateEventType(value: string): string {
  if (value === "start") return "inicio";
  if (value === "end") return "fin";
  if (value === "reading") return "lectura";
  if (value === "track") return "seguimiento";
  return value;
}
